import { useState, useEffect } from 'react';
import api from '../api';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Key,
  Unlock,
  Search,
  X,
  Check,
  AlertCircle,
  Clock,
  UserPlus,
  Bell,
  BellOff,
  Link2,
  Shield,
} from 'lucide-react';
import toast from 'react-hot-toast';

// Staff <-> app-account reconciliation. Cross-references PIN employees with app
// users + their RBAC roles so every staff member lands on the correct role, and
// the two "staff" systems (Staff Manager PINs vs. app accounts) stay aligned.
function StaffReconciliation({ onChanged }) {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [linkingFor, setLinkingFor] = useState(null);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [recRes, rolesRes] = await Promise.all([
        api.get('/admin/staff-pins/reconciliation'),
        api.get('/admin/roles').catch(() => ({ data: { roles: [] } })),
      ]);
      setData(recRes.data);
      setRoles(rolesRes.data.roles || []);
    } catch (err) {
      console.error('Reconciliation load failed', err);
      toast.error('Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (linkingFor == null) return undefined;
    const t = setTimeout(async () => {
      if (!linkQuery || linkQuery.length < 2) { setLinkResults([]); return; }
      try {
        setLinkSearching(true);
        const res = await api.get('/admin/users', { params: { search: linkQuery, limit: 10 } });
        setLinkResults(res.data.users || []);
      } catch (e) {
        // ignore search errors
      } finally {
        setLinkSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [linkQuery, linkingFor]);

  const assignRole = async (userId, roleId) => {
    if (!roleId) return;
    try {
      setBusy(true);
      await api.put(`/admin/users/${userId}/role`, { roleId });
      toast.success('Role updated');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error('Failed to update role');
    } finally {
      setBusy(false);
    }
  };

  const linkAccount = async (employeeNumber, appUserId) => {
    try {
      setBusy(true);
      await api.put(`/admin/staff-pins/${employeeNumber}`, { appUserId });
      toast.success('Linked app account');
      setLinkingFor(null);
      setLinkQuery('');
      setLinkResults([]);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error('Failed to link account');
    } finally {
      setBusy(false);
    }
  };

  const unlinkAccount = async (employeeNumber) => {
    try {
      setBusy(true);
      await api.put(`/admin/staff-pins/${employeeNumber}`, { appUserId: null });
      toast.success('Unlinked app account');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error('Failed to unlink');
    } finally {
      setBusy(false);
    }
  };

  const RoleSelect = ({ user }) => (
    <select
      value={user.roleId || ''}
      disabled={busy}
      onChange={(e) => assignRole(user.id, e.target.value)}
      className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5 text-white text-sm"
    >
      <option value="" disabled>{user.needsRole ? '— Assign role —' : 'Change role…'}</option>
      {roles.map((r) => (
        <option key={r.id} value={r.id}>{r.name}</option>
      ))}
    </select>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="animate-spin text-amber-500" size={28} />
      </div>
    );
  }
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'PIN employees', value: s.pins },
          { label: 'Linked to app', value: s.linked },
          { label: 'PIN, no app account', value: s.pinNoAccount },
          { label: 'App staff, no PIN', value: s.appStaffNoPin },
        ].map((c) => (
          <div key={c.label} className="bg-gray-800 rounded-xl p-4">
            <div className="text-2xl font-bold text-white">{c.value ?? 0}</div>
            <div className="text-gray-400 text-xs mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {s.needsRole > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2 text-amber-400 text-sm">
          <AlertCircle size={16} />
          {s.needsRole} staff {s.needsRole === 1 ? 'account has' : 'accounts have'} no explicit role — assign one so their access is correct.
        </div>
      )}

      {/* App staff — role & access */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-2">
          <Shield size={16} className="text-amber-500" />
          <span className="font-semibold text-white">App staff — role &amp; access</span>
          <span className="text-gray-500 text-sm">({data.appStaffNoPin.length} not linked to a PIN)</span>
        </div>
        {data.appStaffNoPin.length === 0 ? (
          <div className="px-5 py-6 text-gray-500 text-sm">Every app-staff account is linked to a PIN employee.</div>
        ) : (
          <table className="w-full">
            <tbody className="divide-y divide-gray-700">
              {data.appStaffNoPin.map((u) => (
                <tr key={u.id}>
                  <td className="px-5 py-3">
                    <div className="text-white text-sm font-medium">{u.name || u.email || u.id}</div>
                    <div className="text-gray-400 text-xs">{u.email}</div>
                  </td>
                  <td className="px-5 py-3">
                    {u.needsRole ? (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-xs"><AlertCircle size={12} /> No role</span>
                    ) : (
                      <span className="text-gray-300 text-xs">{u.roleName || u.legacyRole}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right"><RoleSelect user={u} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* PIN employees — no app account */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-2">
          <Key size={16} className="text-amber-500" />
          <span className="font-semibold text-white">PIN employees — no app account</span>
          <span className="text-gray-500 text-sm">({data.pinNoAccount.length})</span>
        </div>
        {data.pinNoAccount.length === 0 ? (
          <div className="px-5 py-6 text-gray-500 text-sm">Every PIN employee is linked to an app account.</div>
        ) : (
          <div className="divide-y divide-gray-700">
            {data.pinNoAccount.map((p) => (
              <div key={p.employeeNumber} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm font-medium">
                      {p.employeeName}
                      {p.dangling && <span className="ml-2 text-red-400 text-xs">(broken link)</span>}
                    </div>
                    <div className="text-gray-400 text-xs">#{p.employeeNumber}{p.department ? ` · ${p.department}` : ''}{p.cageRole ? ` · ${p.cageRole}` : ''}</div>
                  </div>
                  <button
                    onClick={() => { setLinkingFor(linkingFor === p.employeeNumber ? null : p.employeeNumber); setLinkQuery(''); setLinkResults([]); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-gray-200 rounded-lg text-sm hover:bg-gray-600"
                  >
                    <Link2 size={14} /> Link app account
                  </button>
                </div>
                {linkingFor === p.employeeNumber && (
                  <div className="mt-3 bg-gray-900 rounded-lg p-3">
                    <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
                      <Search size={14} className="text-gray-400" />
                      <input
                        autoFocus
                        value={linkQuery}
                        onChange={(e) => setLinkQuery(e.target.value)}
                        placeholder="Search users by name, email, or phone…"
                        className="flex-1 bg-transparent text-white text-sm outline-none"
                      />
                      {linkSearching && <RefreshCw size={14} className="animate-spin text-gray-400" />}
                    </div>
                    {linkResults.length > 0 && (
                      <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-gray-800">
                        {linkResults.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => linkAccount(p.employeeNumber, u.id)}
                            className="w-full text-left px-2 py-2 hover:bg-gray-800 rounded"
                          >
                            <div className="text-white text-sm">{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</div>
                            <div className="text-gray-400 text-xs">{u.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-2">
          <Link2 size={16} className="text-green-500" />
          <span className="font-semibold text-white">Linked (PIN ↔ app account)</span>
          <span className="text-gray-500 text-sm">({data.linked.length})</span>
        </div>
        {data.linked.length === 0 ? (
          <div className="px-5 py-6 text-gray-500 text-sm">No PIN employees are linked to an app account yet.</div>
        ) : (
          <table className="w-full">
            <tbody className="divide-y divide-gray-700">
              {data.linked.map((l) => (
                <tr key={l.employeeNumber}>
                  <td className="px-5 py-3">
                    <div className="text-white text-sm font-medium">{l.employeeName}</div>
                    <div className="text-gray-400 text-xs">#{l.employeeNumber}{l.department ? ` · ${l.department}` : ''}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-200 text-sm">{l.appUser?.name || l.appUser?.email}</div>
                    <div className="text-gray-400 text-xs">{l.appUser?.email}</div>
                  </td>
                  <td className="px-5 py-3">
                    {l.appUser?.needsRole ? (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-xs"><AlertCircle size={12} /> No role</span>
                    ) : (
                      <span className="text-gray-300 text-xs">{l.appUser?.roleName || l.appUser?.legacyRole}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {l.appUser && <RoleSelect user={l.appUser} />}
                      <button onClick={() => unlinkAccount(l.employeeNumber)} disabled={busy} className="p-1.5 text-gray-400 hover:text-red-400" title="Unlink">
                        <X size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function StaffManagement() {
  const [staffList, setStaffList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('configured');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Form state
  const [formPin, setFormPin] = useState('');
  const [formConfirmPin, setFormConfirmPin] = useState('');
  const [formDepartment, setFormDepartment] = useState('');
  const [saving, setSaving] = useState(false);

  // VIP-alert recipient state (edit modal)
  const [formVipAlerts, setFormVipAlerts] = useState(false);
  const [linkedUserId, setLinkedUserId] = useState(null);
  const [linkedUserLabel, setLinkedUserLabel] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const searchAppUsers = async (term) => {
    if (!term || term.length < 2) {
      setUserResults([]);
      return;
    }
    try {
      setSearchingUsers(true);
      const res = await api.get('/admin/users', { params: { search: term, limit: 10 } });
      setUserResults(res.data.users || []);
    } catch (err) {
      console.error('User search failed', err);
    } finally {
      setSearchingUsers(false);
    }
  };

  useEffect(() => {
    if (!showEditModal) return undefined;
    const timer = setTimeout(() => searchAppUsers(userSearch), 300);
    return () => clearTimeout(timer);
  }, [userSearch, showEditModal]);

  const fetchData = async () => {
    try {
      const [staffRes, employeesRes] = await Promise.all([
        api.get('/admin/staff-pins', { params: { includeInactive: true } }),
        api.get('/admin/staff-pins/tablecaptain-employees').catch(() => ({ data: { employees: [] } })),
      ]);
      
      setStaffList(staffRes.data.staff || []);
      setEmployees(employeesRes.data.employees || []);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredStaff = staffList.filter(s => {
    const query = searchQuery.toLowerCase();
    return (
      s.employeeName?.toLowerCase().includes(query) ||
      s.employeeNumber?.toLowerCase().includes(query) ||
      s.department?.toLowerCase().includes(query)
    );
  });

  const filteredEmployees = employees.filter(e => {
    if (e.hasPinConfigured) return false;
    const query = searchQuery.toLowerCase();
    return (
      e.employeeName?.toLowerCase().includes(query) ||
      e.employeeNumber?.toLowerCase().includes(query) ||
      e.department?.toLowerCase().includes(query)
    );
  });

  const handleAddStaff = (employee) => {
    setSelectedEmployee(employee);
    setFormPin('');
    setFormConfirmPin('');
    setFormDepartment(employee.department || '');
    setShowAddModal(true);
  };

  const handleEditStaff = (staff) => {
    setSelectedStaff(staff);
    setFormPin('');
    setFormConfirmPin('');
    setFormDepartment(staff.department || '');
    setFormVipAlerts(staff.receivesVipAlerts || false);
    setLinkedUserId(staff.appUserId || null);
    setLinkedUserLabel(staff.appUserName || staff.appUserEmail || '');
    setUserSearch('');
    setUserResults([]);
    setShowEditModal(true);
  };

  const handleCreatePin = async () => {
    if (!selectedEmployee) return;
    
    if (formPin.length < 4 || formPin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    
    if (!/^\d+$/.test(formPin)) {
      toast.error('PIN must contain only numbers');
      return;
    }
    
    if (formPin !== formConfirmPin) {
      toast.error('PINs do not match');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/staff-pins', {
        employeeNumber: selectedEmployee.employeeNumber,
        employeeName: selectedEmployee.employeeName,
        department: formDepartment || undefined,
        pin: formPin,
      });
      
      toast.success(`PIN created for ${selectedEmployee.employeeName}`);
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePin = async () => {
    if (!selectedStaff) return;
    
    if (formPin) {
      if (formPin.length < 4 || formPin.length > 6) {
        toast.error('PIN must be 4-6 digits');
        return;
      }
      
      if (!/^\d+$/.test(formPin)) {
        toast.error('PIN must contain only numbers');
        return;
      }
      
      if (formPin !== formConfirmPin) {
        toast.error('PINs do not match');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {};
      if (formDepartment !== (selectedStaff.department || '')) {
        payload.department = formDepartment || undefined;
      }
      if (formVipAlerts !== (selectedStaff.receivesVipAlerts || false)) {
        payload.receivesVipAlerts = formVipAlerts;
      }
      if ((linkedUserId || null) !== (selectedStaff.appUserId || null)) {
        payload.appUserId = linkedUserId || null;
      }
      if (Object.keys(payload).length > 0) {
        await api.put(`/admin/staff-pins/${selectedStaff.employeeNumber}`, payload);
      }

      if (formPin) {
        await api.put(`/admin/staff-pins/${selectedStaff.employeeNumber}/pin`, {
          pin: formPin,
        });
      }
      
      toast.success(`Updated ${selectedStaff.employeeName}`);
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (staff) => {
    const action = staff.isActive ? 'deactivate' : 'reactivate';
    
    if (!confirm(`Are you sure you want to ${action} ${staff.employeeName}?`)) {
      return;
    }

    try {
      if (staff.isActive) {
        await api.delete(`/admin/staff-pins/${staff.employeeNumber}`);
      } else {
        await api.put(`/admin/staff-pins/${staff.employeeNumber}`, { isActive: true });
      }
      toast.success(`${staff.employeeName} ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${action}`);
    }
  };

  const handleUnlock = async (staff) => {
    try {
      await api.post(`/admin/staff-pins/${staff.employeeNumber}/unlock`);
      toast.success(`${staff.employeeName} unlocked`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to unlock');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Staff Manager</h1>
          <p className="text-gray-400 mt-1">Manage staff PINs for reward redemption</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by name, employee number, or department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('configured')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'configured'
              ? 'bg-amber-500 text-black'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Configured ({staffList.length})
        </button>
        <button
          onClick={() => setActiveTab('available')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'available'
              ? 'bg-amber-500 text-black'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Available ({filteredEmployees.length})
        </button>
        <button
          onClick={() => setActiveTab('reconciliation')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'reconciliation'
              ? 'bg-amber-500 text-black'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Reconciliation
        </button>
      </div>

      {/* Content */}
      {activeTab === 'reconciliation' ? (
        <StaffReconciliation onChanged={fetchData} />
      ) : activeTab === 'configured' ? (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Employee</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Department</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Last Used</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Created</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">VIP Alerts</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No staff PINs configured</p>
                    <p className="text-sm mt-1">Switch to "Available" tab to add staff</p>
                  </td>
                </tr>
              ) : (
                filteredStaff.map((staff) => (
                  <tr key={staff.id} className={`hover:bg-gray-750 ${!staff.isActive ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <div className="font-medium text-white">{staff.employeeName}</div>
                          <div className="text-sm text-gray-400">#{staff.employeeNumber}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {staff.department ? (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-sm rounded">
                          {staff.department}
                        </span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {staff.isActive ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <Check size={16} />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400">
                          <AlertCircle size={16} />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatDate(staff.lastUsedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {formatDate(staff.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      {staff.receivesVipAlerts ? (
                        staff.appUserId ? (
                          <button
                            onClick={() => handleEditStaff(staff)}
                            title={staff.appUserName ? `Alerts go to ${staff.appUserName}` : 'VIP alerts enabled'}
                            className="flex items-center gap-1.5 px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded hover:bg-green-500/30 transition-colors"
                          >
                            <Bell size={14} />
                            On
                          </button>
                        ) : (
                          <button
                            onClick={() => handleEditStaff(staff)}
                            title="Enabled but no app account linked — alerts won't deliver. Click to fix."
                            className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded hover:bg-amber-500/30 transition-colors"
                          >
                            <AlertCircle size={14} />
                            No app link
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => handleEditStaff(staff)}
                          title="Enable VIP arrival alerts for this staff member"
                          className="flex items-center gap-1.5 px-2 py-1 text-gray-500 text-xs rounded hover:bg-gray-700 hover:text-gray-300 transition-colors"
                        >
                          <BellOff size={14} />
                          Off
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditStaff(staff)}
                          className="p-2 text-amber-500 hover:bg-amber-500/20 rounded-lg transition-colors"
                          title="Edit staff (PIN, department, VIP alerts)"
                        >
                          <Key size={18} />
                        </button>
                        <button
                          onClick={() => handleUnlock(staff)}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                          title="Unlock Account"
                        >
                          <Unlock size={18} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(staff)}
                          className={`p-2 rounded-lg transition-colors ${
                            staff.isActive
                              ? 'text-red-400 hover:bg-red-500/20'
                              : 'text-green-400 hover:bg-green-500/20'
                          }`}
                          title={staff.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {staff.isActive ? <Trash2 size={18} /> : <Check size={18} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Employee</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Department</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                    <Check className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>All employees have PINs configured</p>
                    <p className="text-sm mt-1">Or no employees found from TableCaptain</p>
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <div className="font-medium text-white">{employee.employeeName}</div>
                          <div className="text-sm text-gray-400">#{employee.employeeNumber}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {employee.department ? (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-sm rounded">
                          {employee.department}
                        </span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {employee.status || '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleAddStaff(employee)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors ml-auto"
                      >
                        <Plus size={18} />
                        Add PIN
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Create Staff PIN</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Employee</label>
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="font-medium text-white">{selectedEmployee?.employeeName}</div>
                  <div className="text-sm text-gray-400">#{selectedEmployee?.employeeNumber}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Department (Optional)</label>
                <input
                  type="text"
                  value={formDepartment}
                  onChange={(e) => setFormDepartment(e.target.value)}
                  placeholder="e.g., Floor, Management"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">PIN (4-6 digits)</label>
                <input
                  type="password"
                  value={formPin}
                  onChange={(e) => setFormPin(e.target.value)}
                  placeholder="Enter PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Confirm PIN</label>
                <input
                  type="password"
                  value={formConfirmPin}
                  onChange={(e) => setFormConfirmPin(e.target.value)}
                  placeholder="Confirm PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePin}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Edit Staff</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Employee</label>
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="font-medium text-white">{selectedStaff?.employeeName}</div>
                  <div className="text-sm text-gray-400">#{selectedStaff?.employeeNumber}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Department (Optional)</label>
                <input
                  type="text"
                  value={formDepartment}
                  onChange={(e) => setFormDepartment(e.target.value)}
                  placeholder="e.g., Floor, Management"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* VIP Arrival Alerts */}
              <div className="border-t border-gray-700 pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formVipAlerts}
                    onChange={(e) => setFormVipAlerts(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-white">Receives VIP arrival alerts</span>
                    <span className="block text-xs text-gray-400">
                      Get a push notification when a flagged VIP arrives at the casino or is seated.
                    </span>
                  </span>
                </label>

                {formVipAlerts && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Linked app account (receives the push)</label>
                    {linkedUserId ? (
                      <div className="flex items-center justify-between bg-gray-700 rounded-lg px-3 py-2">
                        <span className="text-sm text-white truncate">{linkedUserLabel || 'Linked account'}</span>
                        <button
                          type="button"
                          onClick={() => { setLinkedUserId(null); setLinkedUserLabel(''); setUserSearch(''); }}
                          className="text-gray-400 hover:text-white ml-2 flex-shrink-0"
                          title="Unlink"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          placeholder="Search app users by name or email..."
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                        />
                        {searchingUsers && (
                          <div className="text-xs text-gray-400 mt-1">Searching...</div>
                        )}
                        {userResults.length > 0 && (
                          <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 rounded-lg divide-y divide-gray-600">
                            {userResults.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => {
                                  const label = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                                  setLinkedUserId(u.id);
                                  setLinkedUserLabel(label);
                                  setUserResults([]);
                                  setUserSearch('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-600"
                              >
                                <div className="text-sm text-white">
                                  {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                                </div>
                                <div className="text-xs text-gray-400">{u.email}</div>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex items-start gap-2 mt-2 text-xs text-amber-400">
                          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                          <span>Link an app account so the alert push can be delivered. Without one, this staff member won&apos;t receive VIP alerts.</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">New PIN (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formPin}
                  onChange={(e) => setFormPin(e.target.value)}
                  placeholder="Enter new PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                />
              </div>

              {formPin && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Confirm New PIN</label>
                  <input
                    type="password"
                    value={formConfirmPin}
                    onChange={(e) => setFormConfirmPin(e.target.value)}
                    placeholder="Confirm new PIN"
                    maxLength={6}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePin}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
